import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X, Send, Loader2, Camera, Volume2, VolumeX, Mic } from "lucide-react";
import axios from "axios";

const API = "/api";

const OPENING_MESSAGES = {
  "/": "Hello. I have one question for you. But first — what do you do? Not your title. Your real work. What do you actually do every day?",
  "/submit": "Take your time. There's no right answer. Just yours. I'm here if you need a nudge.",
  "/insights": "Here's what I'm learning from the stories people are sharing with me...",
  "/about": "Every story on this site exists because someone was finally asked why. What's yours?",
};

function getOpeningMessage(pathname) {
  if (pathname.startsWith("/story/")) return "Every person here was brave enough to answer one question. What did this story make you feel?";
  if (pathname === "/icons" || pathname === "/conversations") return "You're in the quiet room. The people here spent whole lifetimes answering the question we ask everyone. Take your time. If something stirs while you read — I'm here, softly.";
  if (pathname.startsWith("/icons/")) return "Sit with this one for a while. A lifetime of why lives on this page. When you're ready — I'd love to hear what it woke up in you.";
  if (pathname === "/because") return "I am because they were. Every why is inherited from someone. As you walk through this room, think about who handed you yours.";
  return OPENING_MESSAGES[pathname] || OPENING_MESSAGES["/"];
}

const CHIP_SETS = {
  // After opening — "What do you do?"
  opening: [
    { label: "Share my why", action: "message", text: "I want to share my why" },
    { label: "I don't know my why yet", action: "message", text: "I don't know my why yet" },
    { label: "Read stories", action: "navigate", to: "/#stories" },
    { label: "Surprise me, Mira", action: "surprise" },
  ],
  // After Mira acknowledges role and asks why
  after_role: [
    { label: "I'll tell you", action: "message", text: "I'll tell you why" },
    { label: "I don't know yet", action: "message", text: "I don't know my why yet" },
    { label: "Give me a moment", action: "message", text: "Give me a moment to think" },
  ],
  // After user shares why and Mira responds with deep follow-up
  after_why: [
    { label: "Yes, exactly", action: "message", text: "Yes, exactly" },
    { label: "It's more than that", action: "message", text: "It's more than that" },
    { label: "Let me think", action: "message", text: "Let me think about that" },
  ],
  // After Mira explains iambecause
  after_explain: [
    { label: "Who started this?", action: "message", text: "Who started iambecause?" },
    { label: "Share my why", action: "message", text: "I want to share my why" },
    { label: "Show me a story", action: "surprise" },
  ],
  // After photo step
  after_photo: [
    { label: "Upload a photo", action: "upload" },
    { label: "Skip for now", action: "message", text: "skip" },
  ],
  // Anonymous choice
  awaiting_anon: [
    { label: "Use my name", action: "message", text: "Use my name", style: "amber" },
    { label: "Stay anonymous", action: "message", text: "Stay anonymous", style: "charcoal" },
  ],
  // Preview confirmation
  awaiting_preview: [
    { label: "Yes, save my story", action: "message", text: "Yes, save my story", style: "amber" },
    { label: "Let me change something", action: "message", text: "Let me change something", style: "charcoal" },
  ],
  // After save
  after_save: [
    { label: "Read other stories", action: "navigate", to: "/#stories" },
    { label: "Share my story", action: "message", text: "How can I share my story?" },
    { label: "Ask Mira something", action: "message", text: "Tell me something about the stories you've heard" },
  ],
  // After surprise story
  after_surprise: [
    { label: "Tell me another", action: "surprise" },
    { label: "Share my why", action: "message", text: "I want to share my why" },
    { label: "What patterns do you see?", action: "message", text: "What patterns do you see across the stories?" },
  ],
  // After insight
  after_insight: [
    { label: "Tell me another", action: "surprise" },
    { label: "Share my why", action: "message", text: "I want to share my why" },
    { label: "What patterns do you see?", action: "message", text: "What patterns do you see?" },
  ],
  // Photo retry
  photo_retry: [
    { label: "Try again", action: "message", text: "Try again" },
    { label: "Continue without photo", action: "message", text: "Continue without photo" },
  ],
  // Seeking mode — gentle exploration
  seeking: [
    { label: "Tell me more", action: "message", text: "Tell me more" },
    { label: "Ask me another", action: "message", text: "Ask me another question" },
    { label: "I think I found it", action: "message", text: "I think I found something" },
  ],
  // Seeking reflect — after Mira reflects back
  seeking_reflect: [
    { label: "Yes, that feels true", action: "message", text: "Yes, that feels true", style: "amber" },
    { label: "Not quite — let me think", action: "message", text: "Not quite, let me think", style: "charcoal" },
  ],
  seeking_save: [
    { label: "Yes, save my searching", action: "message", text: "Yes, save my searching", style: "amber" },
    { label: "Not yet", action: "message", text: "Not yet", style: "charcoal" },
  ],
  // After declining to save seeking
  seeking_exit: [
    { label: "Read stories", action: "navigate", to: "/#stories" },
    { label: "Surprise me, Mira", action: "surprise" },
  ],
  // No chips — name, city, deep, edit steps
  none: [],
};

function SparkleIcon({ size = 24, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
      <path d="M12 2L13.09 8.26L18 6L14.74 10.91L21 12L14.74 13.09L18 18L13.09 15.74L12 22L10.91 15.74L6 18L9.26 13.09L3 12L9.26 10.91L6 6L10.91 8.26L12 2Z" fill="currentColor" />
    </svg>
  );
}

function wordCount(text) {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

export default function MiraChat({ startInSeekingMode = false }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [chipSet, setChipSet] = useState("opening");
  const [showChips, setShowChips] = useState(true);

  // Story capture state machine
  // idle → awaiting_role → awaiting_why → awaiting_deep → awaiting_name → awaiting_city → awaiting_photo → awaiting_anon → awaiting_preview → saving → saved
  const [captureState, setCaptureState] = useState("idle");
  const capturedStory = useRef({ why: "", polishedWhy: "", followupAnswer: "", name: "", role: "", city: "", email: "", whatsapp: "", media_url: "", anonymous: false, spokenFields: 0, typedFields: 0, stage: null, miraReflection: "" });
  const [showPreview, setShowPreview] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Seeking mode
  const [seekingMode, setSeekingMode] = useState(false);
  const [seekingExchanges, setSeekingExchanges] = useState(0);
  const seekingSessionRef = useRef(null);
  const seekingTranscriptRef = useRef([]);
  const seekingReflectionRef = useRef("");

  // Voice state
  const [voiceOn, setVoiceOn] = useState(true);
  const [speaking, setSpeaking] = useState(false);
  const audioRef = useRef(null);
  const speechSynthRef = useRef(null);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Check browser support for SpeechRecognition
  const SpeechRecognition = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
  const hasSpeechSupport = !!SpeechRecognition;

  // Voice input (mic) state
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const lastSpokenRef = useRef(false); // tracks if the CURRENT input was spoken

  const startListening = () => {
    if (!SpeechRecognition || listening) return;
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-IN";

    let fullTranscript = "";

    recognition.onresult = (event) => {
      let current = "";
      for (let i = 0; i < event.results.length; i++) {
        current += event.results[i][0].transcript;
      }
      fullTranscript = current;
      setInput(current.trim());

      // Reset silence timer — auto-send after 1.5s of silence
      if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = setTimeout(() => {
        // Stop recognition — this triggers onend
        if (recognitionRef.current) {
          recognitionRef.current.stop();
        }
      }, 1500);
    };

    recognition.onend = () => {
      setListening(false);
      if (silenceTimerRef.current) { clearTimeout(silenceTimerRef.current); silenceTimerRef.current = null; }
      const text = fullTranscript.trim();
      if (text) {
        lastSpokenRef.current = true;
        capturedStory.current.spokenFields += 1;
        setTimeout(() => {
          setInput("");
          sendMessage(text);
        }, 200);
      }
      recognitionRef.current = null;
    };

    recognition.onerror = (e) => {
      // Try en-US fallback if en-IN fails
      if (e.error === "language-not-supported" && recognition.lang === "en-IN") {
        recognition.lang = "en-US";
        try { recognition.start(); return; } catch {}
      }
      setListening(false);
      if (silenceTimerRef.current) { clearTimeout(silenceTimerRef.current); silenceTimerRef.current = null; }
      recognitionRef.current = null;
    };

    recognitionRef.current = recognition;
    setInput("");
    lastSpokenRef.current = false;
    recognition.start();
    setListening(true);
  };

  const stopListening = () => {
    if (silenceTimerRef.current) { clearTimeout(silenceTimerRef.current); silenceTimerRef.current = null; }
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
  };

  const toggleMic = () => {
    if (listening) {
      stopListening();
    } else {
      startListening();
    }
  };

  // Speak text using ElevenLabs (or Web Speech API fallback)
  const speakText = useCallback(async (text) => {
    if (!voiceOn) return;

    // Stop any current speech
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    if (speechSynthRef.current) { window.speechSynthesis.cancel(); speechSynthRef.current = null; }

    // Clean text for speech (remove quotes, newlines)
    const cleanText = text
      .replace(/\*\*/g, '')
      .replace(/\*/g, '')
      .replace(/__/g, '')
      .replace(/_/g, '')
      .replace(/#{1,6}\s*/g, '')
      .replace(/["""\n]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (!cleanText) return;

    setSpeaking(true);

    try {
      const res = await fetch(`${API}/mira/speak`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: cleanText }),
      });

      if (!res.ok) throw new Error("TTS failed");

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;

      audio.onended = () => { setSpeaking(false); URL.revokeObjectURL(url); audioRef.current = null; };
      audio.onerror = () => { setSpeaking(false); URL.revokeObjectURL(url); audioRef.current = null; };
      await audio.play();
    } catch {
      // Fallback to Web Speech API silently — always pick a female voice
      try {
        const utterance = new SpeechSynthesisUtterance(cleanText);
        const voices = window.speechSynthesis.getVoices() || [];
        const prefs = ["female", "samantha", "zira", "veena", "heera", "google uk english female", "victoria", "karen", "moira", "tessa", "kate", "serena"];
        let chosen = null;
        for (const p of prefs) {
          chosen = voices.find(v => v.name.toLowerCase().includes(p));
          if (chosen) break;
        }
        if (chosen) utterance.voice = chosen;
        utterance.rate = 0.9;
        utterance.pitch = 1.1;
        utterance.onend = () => { setSpeaking(false); speechSynthRef.current = null; };
        utterance.onerror = () => { setSpeaking(false); speechSynthRef.current = null; };
        speechSynthRef.current = utterance;
        window.speechSynthesis.speak(utterance);
      } catch {
        setSpeaking(false);
      }
    }
  }, [voiceOn]);

  // Toggle voice
  const toggleVoice = () => {
    if (voiceOn) {
      // Turn off — stop current speech
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
      window.speechSynthesis?.cancel();
      setSpeaking(false);
    }
    setVoiceOn(!voiceOn);
  };

  // Persist session across panel opens using localStorage
  const getStoredSession = () => {
    try {
      const stored = localStorage.getItem("mira_session");
      if (stored) {
        const data = JSON.parse(stored);
        if (Date.now() - data.timestamp < 24 * 60 * 60 * 1000) return data;
      }
    } catch {}
    return null;
  };

  const saveSession = (msgs, sid, capState, story) => {
    try {
      localStorage.setItem("mira_session", JSON.stringify({
        messages: msgs.slice(-20),
        sessionId: sid,
        captureState: capState,
        capturedStory: story,
        timestamp: Date.now(),
      }));
    } catch {}
  };

  const clearStoredSession = () => {
    try { localStorage.removeItem("mira_session"); } catch {}
  };

  // Reset fresh every time Mira opens
  useEffect(() => {
    if (open) {
      const stored = getStoredSession();
      if (stored && stored.messages?.length > 1 && !startInSeekingMode) {
        setMessages(stored.messages);
        setSessionId(stored.sessionId);
        setCaptureState(stored.captureState || "idle");
        if (stored.capturedStory) {
          Object.assign(capturedStory.current, stored.capturedStory);
        }
        setChipSet("opening");
        setShowChips(true);
        setSeekingMode(false);
        setSeekingExchanges(0);
        seekingSessionRef.current = null;
        seekingTranscriptRef.current = [];
        seekingReflectionRef.current = "";
      } else {
        const opening = startInSeekingMode
          ? "Hello. I heard you're still searching. That's the most honest place to begin. Tell me — what are you doing right now, even if it doesn't feel like enough yet?"
          : getOpeningMessage(location.pathname);
        setMessages([{ role: "mira", text: opening }]);
        setChipSet(startInSeekingMode ? "seeking" : "opening");
        setShowChips(true);
        setSessionId(null);
        setCaptureState("idle");
        capturedStory.current = { why: "", polishedWhy: "", followupAnswer: "", name: "", role: "", city: "", media_url: "", anonymous: false, spokenFields: 0, typedFields: 0, stage: null, miraReflection: "" };
        if (startInSeekingMode) {
          setSeekingMode(true);
          setSeekingExchanges(0);
          seekingTranscriptRef.current = [{ role: "mira", text: opening }];
        } else {
          setSeekingMode(false);
          setSeekingExchanges(0);
        }
        seekingSessionRef.current = null;
        seekingReflectionRef.current = "";
        clearStoredSession();
        setTimeout(() => speakText(opening), 500);
      }
    } else {
      // Stop speech when closing
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
      window.speechSynthesis?.cancel();
      setSpeaking(false);
    }
  }, [open, location.pathname, speakText]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, showChips]);

  // Persist session to localStorage whenever messages change
  useEffect(() => {
    if (messages.length > 1 && open) {
      saveSession(messages, sessionId, captureState, {
        why: capturedStory.current.why,
        polishedWhy: capturedStory.current.polishedWhy,
        followupAnswer: capturedStory.current.followupAnswer,
        name: capturedStory.current.name,
        role: capturedStory.current.role,
        city: capturedStory.current.city,
        email: capturedStory.current.email,
        stage: capturedStory.current.stage,
      });
    }
  }, [messages, sessionId, captureState, open]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 300);
  }, [open]);

  const addMira = (text, newChipSet, speak = true) => {
    setMessages((prev) => [...prev, { role: "mira", text }]);
    if (newChipSet !== undefined) {
      setChipSet(newChipSet);
      setShowChips(false);
      setTimeout(() => setShowChips(true), 400);
    } else {
      setShowChips(false);
    }
    if (speak) speakText(text);
  };

  const addUser = (text) => {
    setMessages((prev) => [...prev, { role: "user", text }]);
    setShowChips(false);
  };

  // Upload photo to Cloudinary
  const handlePhotoUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    addUser("Uploading photo...");
    try {
      const sigRes = await axios.get(`${API}/cloudinary/signature?resource_type=image`);
      const { signature, timestamp, cloud_name, api_key, folder } = sigRes.data;
      const formData = new FormData();
      formData.append("file", file);
      formData.append("signature", signature);
      formData.append("timestamp", timestamp);
      formData.append("api_key", api_key);
      formData.append("folder", folder);
      const uploadRes = await axios.post(
        `https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`,
        formData
      );
      capturedStory.current.media_url = uploadRes.data.secure_url;
      addMira("Beautiful. I can see you now.", null);
      setCaptureState("awaiting_anon");
      setTimeout(() => {
        addMira("Last thing — and only because some people prefer it this way — would you like your story to appear with your name, or quietly, as just a storyteller? Both are equally beautiful.", "awaiting_anon");
      }, 800);
    } catch (err) {
      console.error("Mira photo upload failed:", err);
      setCaptureState("awaiting_photo_retry");
      addMira("It looks like the photo didn't make it through — sometimes that happens. Would you like to try again, or shall we continue without it? Your words are already the whole story.", "photo_retry");
      return;
    } finally {
      setUploading(false);
    }
  };

  const saveStory = async () => {
    setCaptureState("saving");
    setLoading(true);
    try {
      const s = capturedStory.current;
      const originalWhy = s.followupAnswer ? s.why + "\n\n" + s.followupAnswer : s.why;
      const finalWhy = s.polishedWhy || originalWhy;

      // Determine source: all spoken, all typed, or mixed
      let source = "mira_chat";
      if (s.spokenFields > 0 && s.typedFields === 0) source = "mira_voice";
      else if (s.spokenFields > 0 && s.typedFields > 0) source = "mira_mixed";

      const data = {
        name: s.name,
        role: s.role || "—",
        why: finalWhy,
        why_original: originalWhy,
        city: s.city,
        email: s.email || null,
        whatsapp: s.whatsapp || null,
        anonymous: s.anonymous,
        media_type: source === "mira_voice" ? "voice" : "mira_chat",
        source: source,
      };
      // Seeking stories — override source and add reflection
      if (s.stage === "seeking") {
        data.stage = "seeking";
        data.source = "mira_seeking";
        if (s.miraReflection) {
          data.mira_reflection = s.miraReflection;
        }
        // Save raw transcript as why_original
        if (seekingTranscriptRef.current.length > 0) {
          data.why_original = seekingTranscriptRef.current.map(t => `${t.role === "user" ? "Them" : "Mira"}: ${t.text}`).join("\n");
        }
      }
      if (s.media_url) {
        data.media_url = s.media_url;
        data.media_type = "image";
      }
      const storyRes = await axios.post(`${API}/stories`, data);
      setCaptureState("saved");
      clearStoredSession();
      const name = s.name;
      const storyId = storyRes.data?.id;

      // Build Mirror message
      let mirrorMsg = "";
      if (storyId) {
        try {
          const tokenRes = await axios.get(`${API}/mirror/verify/${storyId}`);
          // We won't have a valid token this way — generate it from the pattern
        } catch {}
        // The mirror token is generated server-side from story ID
        // We'll show the link generically — the user can access it when the story is approved
        mirrorMsg = "\n\nWhen your story is approved — I have one more conversation for you. Not for the world. Just for you. It's called The Mirror. I'll send you the link.";
      }

      if (s.anonymous) {
        addMira(`Your story is on the wall — quietly, the way you wanted it. Someone is going to read it and feel less alone because of what you shared. Thank you for trusting iambecause with your why.${mirrorMsg}`, "after_save");
      } else {
        addMira(`${name}, your story is on the wall. Someone is going to read it and feel less alone because of what you shared. Thank you for trusting iambecause with your why.${mirrorMsg}`, "after_save");
      }
    } catch {
      setCaptureState("idle");
      addMira("Something went wrong saving your story. You could try the submit page instead — your words deserve to be heard.", "opening");
    } finally {
      setLoading(false);
    }
  };

  // --- Input validation for each step ---
  const isIncomplete = (text) => {
    const t = text.trim().toLowerCase();
    const wc = wordCount(text);
    // Single characters, greetings, filler words, or obviously incomplete
    const junk = ["ok", "yes", "no", "hi", "hello", "hey", "hm", "hmm", "idk", "lol", "haha", "thanks", "thank you", "sure", "cool", "nice", "wow", "oh", "um", "uh", "k", "ya", "yeah", "yep", "nope", "test", "asdf", "...", "..", "?", "!"];
    if (junk.includes(t) || t.length < 3) return true;
    // Very short + no real words (like "its been", "the", "a")
    if (wc <= 2 && !t.includes("nurse") && !t.includes("chef") && !t.includes("teacher") && !t.includes("doctor")) {
      // Check if it could be a name (capitalized, 1-3 words) — names are valid for name step
      return true;
    }
    return false;
  };

  const isValidRole = (text) => {
    const t = text.trim();
    if (t.length < 2) return false;
    // Must not be junk
    const lower = t.toLowerCase();
    const junk = ["ok", "yes", "no", "hi", "hello", "hey", "idk", "test", "its been", "it's been", "lol", "hmm", "um"];
    if (junk.some(j => lower === j || lower === j + ".")) return false;
    // At least one real word
    return wordCount(t) >= 1 && t.length >= 3;
  };

  const isValidWhy = (text) => {
    const t = text.trim();
    const wc = wordCount(t);
    const lower = t.toLowerCase();
    const junk = ["ok", "yes", "no", "idk", "i don't know", "sure", "hmm", "test", "hello"];
    if (junk.includes(lower)) return false;
    // Needs at least 4 words to be a real why
    return wc >= 4;
  };

  const isValidName = (text) => {
    const t = text.trim();
    if (t.length < 2) return false;
    const lower = t.toLowerCase();
    const junk = ["ok", "yes", "no", "hi", "hello", "idk", "test", "hmm", "skip"];
    return !junk.includes(lower);
  };

  const isValidCity = (text) => {
    const t = text.trim();
    if (t.length < 2) return false;
    const lower = t.toLowerCase();
    const junk = ["ok", "yes", "no", "hi", "hello", "idk", "test", "hmm", "skip"];
    return !junk.includes(lower);
  };

  const gentleRoleNudge = () => {
    const nudges = [
      "That sounds like the beginning of a thought that got away from you. Take your time — what is it that you actually do? Not the title, just the real thing.",
      "I think that one escaped mid-sentence. No rush — tell me what you do. The thing that fills your days.",
    ];
    return nudges[Math.floor(Math.random() * nudges.length)];
  };

  const gentleWhyNudge = () => {
    const nudges = [
      "That's a big question and there's no rush. What made you show up today — to this work, to this life? Even just one true sentence is enough.",
      "Take your time with this one. There's no word limit and no wrong answer. Why do you do what you do?",
    ];
    return nudges[Math.floor(Math.random() * nudges.length)];
  };

  const gentleRetry = () => {
    const nudges = [
      "It looks like your thought might not have made it all the way through. Say it again — I'm listening.",
      "I think that got cut short. Try again — I'm right here.",
    ];
    return nudges[Math.floor(Math.random() * nudges.length)];
  };

  // Detect if user wants to share their story (explicit intent)
  const isShareIntent = (text) => {
    const lower = text.toLowerCase();
    return lower.includes("share my why") || lower.includes("want to share") || lower.includes("i'll share") || lower.includes("my story") || lower.includes("tell you why") || lower.includes("here's why") || lower.includes("let me share");
  };

  // Generate dynamic deep follow-up via Claude (uses both what + why)
  const getDeepFollowup = async (whatText, whyText) => {
    try {
      const res = await axios.post(`${API}/mira/followup`, { what: whatText, why: whyText });
      return res.data.response;
    } catch {
      return "That took courage to share. Was there a moment — a single moment — that made you know this was yours to do?";
    }
  };

  // Generate warm acknowledgement of their role via Claude
  const getWarmAcknowledgement = async (roleText) => {
    try {
      const res = await axios.post(`${API}/mira/acknowledge`, { role: roleText });
      return res.data.response;
    } catch {
      return `A ${roleText.toLowerCase()}. That's not a job — that's a way of being in the world. Now tell me — why? Why do you do that? Not the official answer. The real one.`;
    }
  };

  const handleCaptureFlow = async (text) => {
    // User expressed explicit intent to share — start with What
    if (captureState === "idle" && isShareIntent(text)) {
      setCaptureState("awaiting_role");
      addMira("I'd love to hear it. But first — what do you do? Not your title. Your real work. What do you actually do every day?", "none");
      return true;
    }

    // Seeking mode active or seeking intent — let sendMessage handle it
    if (captureState === "idle") {
      if (seekingMode) return false;
      const lower = text.trim().toLowerCase();
      if (lower.includes("don't know") || lower.includes("dont know") || lower.includes("not sure") || lower.includes("still searching") || lower.includes("still figuring") || lower.includes("haven't found") || lower.includes("havent found") || lower.includes("no idea")) {
        return false;
      }
    }

    // User told Mira what they do on homepage opening (idle)
    if (captureState === "idle" && text.trim().length >= 2) {
      if (!isValidRole(text)) {
        addMira(gentleRoleNudge(), "none");
        return true;
      }
      let role = text.trim();
      const prefixes = /^(i am a |i'm a |i am an |i'm an |i work as a |i work as an |i'm |i am )/i;
      role = role.replace(prefixes, "").trim();
      capturedStory.current.role = role;
      setCaptureState("awaiting_why");
      setLoading(true);
      const ack = await getWarmAcknowledgement(text.trim());
      setLoading(false);
      addMira(ack, "after_role");
      return true;
    }

    // STEP: awaiting_role (from share intent path)
    if (captureState === "awaiting_role") {
      if (!isValidRole(text)) {
        addMira(gentleRoleNudge(), "none");
        return true;
      }
      let role = text.trim();
      const prefixes = /^(i am a |i'm a |i am an |i'm an |i work as a |i work as an |i'm |i am )/i;
      role = role.replace(prefixes, "").trim();
      capturedStory.current.role = role;
      setCaptureState("awaiting_why");
      setLoading(true);
      const ack = await getWarmAcknowledgement(text.trim());
      setLoading(false);
      addMira(ack, "after_role");
      return true;
    }

    // STEP 2: User shared their why
    if (captureState === "awaiting_why") {
      if (!isValidWhy(text)) {
        addMira(gentleWhyNudge(), "after_role");
        return true;
      }
      capturedStory.current.why = text.trim();
      setLoading(true);
      const deepResponse = await getDeepFollowup(capturedStory.current.role, text.trim());
      setLoading(false);
      setCaptureState("awaiting_deep");
      addMira(deepResponse, "after_why");
      return true;
    }

    // STEP 3 response: User answered the deep follow-up
    if (captureState === "awaiting_deep") {
      if (isIncomplete(text) && wordCount(text) < 3) {
        addMira(gentleRetry(), "after_why");
        return true;
      }
      capturedStory.current.followupAnswer = text.trim();
      setCaptureState("awaiting_name");
      addMira("Thank you for that. And what do people call you?", "none");
      return true;
    }

    // STEP 4: User shared their name
    if (captureState === "awaiting_name") {
      if (!isValidName(text)) {
        addMira("I didn't quite catch that. What's your name?", "none");
        return true;
      }
      capturedStory.current.name = text.trim();
      // For seeking stories, ask what they do before asking city
      if (capturedStory.current.stage === "seeking" && !capturedStory.current.role) {
        setCaptureState("awaiting_seeking_role");
        addMira(`${text.trim()}. And what do you do — even if you're not sure it's the right thing yet?`, "none");
        return true;
      }
      setCaptureState("awaiting_city");
      addMira(`${text.trim()}. I'll remember that. Where are you in the world right now?`, "none");
      return true;
    }

    // STEP 5: User shared their city
    if (captureState === "awaiting_city") {
      if (!isValidCity(text)) {
        addMira("Which city or town are you in? Anywhere in the world.", "none");
        return true;
      }
      capturedStory.current.city = text.trim();
      setCaptureState("awaiting_email");
      addMira("One last thing — what is your email? I'll send you something private when your story is live. Just for you. Never shown publicly, never shared with anyone.", "none");
      return true;
    }

    // Email collection
    if (captureState === "awaiting_email") {
      const emailText = text.trim().toLowerCase();
      if (emailText.includes("@") && emailText.includes(".")) {
        capturedStory.current.email = emailText;
        const name = capturedStory.current.name;
        setCaptureState("awaiting_photo");
        addMira(`Thank you. ${name} from ${capturedStory.current.city}. Your story is already something. Would you like to put a face to it? A photo — or just type skip. Your words are more than enough.`, "after_photo");
      } else if (emailText.includes("no") || emailText.includes("skip") || emailText.includes("rather not") || emailText.includes("prefer not")) {
        setCaptureState("awaiting_whatsapp");
        addMira("I understand. Could I have your WhatsApp number instead? I need one of the two — email or WhatsApp — so your story stays connected to you. Never public, never shared with anyone.", "none");
      } else {
        addMira("I promise — it goes nowhere except to you. No newsletters. No marketing. Just your Mirror, when it's ready. What is your email? (Or type skip and I'll take your WhatsApp instead.)", "none");
      }
      return true;
    }

    // WhatsApp collection (when email was declined)
    if (captureState === "awaiting_whatsapp") {
      const digits = text.replace(/\D/g, "");
      if (digits.length >= 8) {
        capturedStory.current.whatsapp = text.trim();
        const name = capturedStory.current.name;
        setCaptureState("awaiting_photo");
        addMira(`Thank you. ${name} from ${capturedStory.current.city}. Your story is already something. Would you like to put a face to it? A photo — or just type skip. Your words are more than enough.`, "after_photo");
      } else if (text.trim().toLowerCase().includes("email") || (text.includes("@") && text.includes("."))) {
        const maybeEmail = text.trim().toLowerCase();
        if (maybeEmail.includes("@") && maybeEmail.includes(".")) {
          capturedStory.current.email = maybeEmail;
          const name = capturedStory.current.name;
          setCaptureState("awaiting_photo");
          addMira(`Thank you. ${name} from ${capturedStory.current.city}. Would you like to put a face to your story? A photo — or just type skip.`, "after_photo");
        } else {
          setCaptureState("awaiting_email");
          addMira("Of course — what is your email?", "none");
        }
      } else {
        addMira("I really do need one of the two — your email or your WhatsApp number. Whichever feels safer. It's the only thread between you and your story.", "none");
      }
      return true;
    }

    // Photo retry
    if (captureState === "awaiting_photo_retry") {
      const lower = text.toLowerCase();
      if (lower.includes("try again") || lower.includes("retry") || lower.includes("upload")) {
        setCaptureState("awaiting_photo");
        addMira("Let's try that again. Upload your photo or type skip.", "after_photo");
      } else {
        // Continue without photo
        setCaptureState("awaiting_anon");
        addMira("Last thing — and only because some people prefer it this way — would you like your story to appear with your name, or quietly, as just a storyteller? Both are equally beautiful.", "awaiting_anon");
      }
      return true;
    }

    // STEP 5 response: Photo or skip
    if (captureState === "awaiting_photo") {
      setCaptureState("awaiting_anon");
      addMira(`Last thing — and only because some people prefer it this way — would you like your story to appear with your name, or quietly, as just a storyteller? Both are equally beautiful.`, "awaiting_anon");
      return true;
    }

    // STEP 6: Anonymous choice → polish text → show preview
    if (captureState === "awaiting_anon") {
      const lower = text.toLowerCase();
      capturedStory.current.anonymous = lower.includes("anon") || lower.includes("quiet") || lower.includes("storyteller") || lower.includes("private") || lower.includes("hidden");
      // Polish the why text with Claude
      setLoading(true);
      try {
        const fullWhy = capturedStory.current.followupAnswer
          ? capturedStory.current.why + "\n\n" + capturedStory.current.followupAnswer
          : capturedStory.current.why;
        const res = await axios.post(`${API}/mira/polish`, { text: fullWhy });
        capturedStory.current.polishedWhy = res.data.polished;
      } catch {
        capturedStory.current.polishedWhy = capturedStory.current.why;
      }
      setLoading(false);
      setCaptureState("awaiting_preview");
      setShowPreview(true);
      setShowVersions(false);
      addMira("Here is how your story will appear on the wall. I've tidied it very gently — your words, your truth, just a little cleaner. Does this feel right?", "awaiting_preview");
      return true;
    }

    // STEP 7: Preview confirmation
    if (captureState === "awaiting_preview") {
      const lower = text.toLowerCase();
      if (lower.includes("yes") || lower.includes("save") || lower.includes("perfect") || lower.includes("looks good") || lower.includes("go ahead") || lower.includes("publish")) {
        setShowPreview(false);
        setShowVersions(false);
        await saveStory();
      } else if (lower.includes("change") || lower.includes("edit") || lower.includes("adjust") || lower.includes("fix") || lower.includes("wrong") || lower.includes("original")) {
        setShowPreview(false);
        setShowVersions(true);
        setCaptureState("awaiting_version_choice");
        addMira("Of course. Here are both versions — choose the one that feels most like you, or type your own.", "none");
      } else {
        setShowPreview(false);
        setShowVersions(false);
        await saveStory();
      }
      return true;
    }

    // STEP 7b: Version choice — user picks original, polished, or types their own
    if (captureState === "awaiting_version_choice") {
      const lower = text.toLowerCase();
      if (lower.includes("original") || lower.includes("my words") || lower.includes("first") || lower.includes("raw")) {
        capturedStory.current.polishedWhy = capturedStory.current.followupAnswer
          ? capturedStory.current.why + "\n\n" + capturedStory.current.followupAnswer
          : capturedStory.current.why;
        setShowVersions(false);
        setCaptureState("awaiting_preview");
        setShowPreview(true);
        addMira("Your original words, exactly as you said them. Does this feel right?", "awaiting_preview");
      } else if (lower.includes("polished") || lower.includes("clean") || lower.includes("tidied") || lower.includes("second") || lower.includes("mira")) {
        setShowVersions(false);
        setCaptureState("awaiting_preview");
        setShowPreview(true);
        addMira("The gently polished version. Does this feel right?", "awaiting_preview");
      } else {
        // They typed their own version
        capturedStory.current.polishedWhy = text.trim();
        setShowVersions(false);
        setCaptureState("awaiting_preview");
        setShowPreview(true);
        addMira("Your words, your way. Does this feel right?", "awaiting_preview");
      }
      return true;
    }

    // STEP 7c: Edit mode — user says what to change
    if (captureState === "awaiting_edit") {
      const lower = text.toLowerCase();
      if (lower.includes("name")) {
        setCaptureState("awaiting_name");
        addMira("What would you like your name to be?", "none");
      } else if (lower.includes("city") || lower.includes("where") || lower.includes("location")) {
        setCaptureState("awaiting_city");
        addMira("Which city should I use?", "none");
      } else if (lower.includes("why") || lower.includes("word") || lower.includes("story") || lower.includes("text")) {
        setCaptureState("awaiting_why");
        addMira("Tell me your why again — take your time.", "none");
      } else if (lower.includes("role") || lower.includes("what i do") || lower.includes("work") || lower.includes("job")) {
        setCaptureState("awaiting_role");
        addMira("What do you do? Tell me again.", "none");
      } else {
        // They typed the correction directly — update the why
        capturedStory.current.why = text.trim();
        setCaptureState("awaiting_preview");
        setShowPreview(true);
        addMira("Updated. Here's how it looks now:", "awaiting_preview");
      }
      return true;
    }

    // SEEKING: User articulates their emerging why
    if (captureState === "seeking_articulate") {
      if (text.trim().length < 10) {
        addMira("Take your time. Even one sentence is enough — what is it you're beginning to see?", "none");
        return true;
      }
      capturedStory.current.why = text.trim();
      capturedStory.current.stage = "seeking";
      setCaptureState("awaiting_name");
      addMira("Thank you for that beginning. What do people call you?", "none");
      return true;
    }

    // SEEKING: Ask for role during seeking capture
    if (captureState === "awaiting_seeking_role") {
      capturedStory.current.role = text.trim();
      setCaptureState("awaiting_city");
      addMira("Where are you in the world right now?", "none");
      return true;
    }

    return false;
  };

  const sendMessage = async (text) => {
    if (!text.trim()) return;
    addUser(text.trim());
    setInput("");

    // Track typed vs spoken for source detection
    if (!lastSpokenRef.current && captureState !== "idle") {
      capturedStory.current.typedFields += 1;
    }
    lastSpokenRef.current = false;

    // Capture flow handles everything when active
    if (await handleCaptureFlow(text.trim())) return;

    setLoading(true);

    // Detect seeking intent
    const lower = text.trim().toLowerCase();
    const isSeekingIntent = lower.includes("don't know") || lower.includes("dont know") || lower.includes("not sure") || lower.includes("still searching") || lower.includes("still figuring") || lower.includes("haven't found") || lower.includes("havent found") || lower.includes("no idea") || lower.includes("don't know my why") || lower.includes("something is missing");

    if (isSeekingIntent && !seekingMode) {
      setSeekingMode(true);
      setSeekingExchanges(0);
      seekingTranscriptRef.current = [{ role: "user", text: text.trim() }];
      try {
        const res = await axios.post(`${API}/mira/seeking`, { message: text.trim(), exchange_count: 0 });
        seekingSessionRef.current = res.data.session_id;
        setSeekingExchanges(1);
        const openingMsg = "Hello. I heard you're still searching. That's the most honest place to begin. Tell me — what are you doing right now, even if it doesn't feel like enough yet?";
        seekingTranscriptRef.current.push({ role: "mira", text: openingMsg });
        addMira(openingMsg, "seeking");
      } catch {
        const fallback = "Hello. I heard you're still searching. That's the most honest place to begin. What are you doing right now?";
        seekingTranscriptRef.current.push({ role: "mira", text: fallback });
        addMira(fallback, "seeking");
      }
      setLoading(false);
      return;
    }

    // Seeking mode — continue conversation
    if (seekingMode) {
      seekingTranscriptRef.current.push({ role: "user", text: text.trim() });
      const newCount = seekingExchanges + 1;
      
      // Check if they've found something
      const foundIt = lower.includes("i think i found") || lower.includes("that's it") || lower.includes("thats it") || lower.includes("yes that's true") || lower.includes("yes, that") || lower.includes("that sounds right") || lower.includes("that's my why") || lower.includes("i found it");
      
      if (foundIt) {
        // They sense something emerging — ask them to put it into words
        setSeekingMode(false);
        capturedStory.current.stage = "seeking";
        setCaptureState("seeking_articulate");
        addMira("That's beautiful. Can you put it into words — even just a beginning? What is the thing you're starting to see?", "none");
        setLoading(false);
        return;
      }

      // Handle reflect phase responses
      if (captureState === "seeking_reflect_offered") {
        const isYes = lower.includes("yes") || lower.includes("true") || lower.includes("that feels") || lower.includes("exactly");
        if (isYes) {
          // They confirmed the reflection — offer to save
          setCaptureState("seeking_save_offered");
          addMira("Would you like to save this moment? Not as a finished story — as a beginning. The searching is part of your why too.", "seeking_save");
          setLoading(false);
          return;
        } else {
          // Not quite — ask one more question and try again
          setCaptureState("idle");
          // Continue to the seeking conversation below
        }
      }

      // Handle save offer responses
      if (captureState === "seeking_save_offered") {
        const isYes = lower.includes("yes") || lower.includes("save") || lower.includes("sure") || lower.includes("please");
        if (isYes) {
          // Generate conversation summary and save
          setSeekingMode(false);
          capturedStory.current.stage = "seeking";
          capturedStory.current.role = "Still finding my why";
          
          // Build transcript text
          const transcriptText = seekingTranscriptRef.current.map(t => `${t.role === "user" ? "Them" : "Mira"}: ${t.text}`).join("\n");
          
          try {
            // Generate summary and use existing reflection
            const sumRes = await axios.post(`${API}/mira/summarize-seeking`, { conversation: transcriptText });
            capturedStory.current.why = sumRes.data.summary;
            capturedStory.current.polishedWhy = sumRes.data.summary;
          } catch {
            // Fallback — use the last meaningful user message
            const userMsgs = seekingTranscriptRef.current.filter(t => t.role === "user");
            capturedStory.current.why = userMsgs.map(m => m.text).join(" ");
            capturedStory.current.polishedWhy = capturedStory.current.why;
          }
          
          // Store raw transcript as why_original
          capturedStory.current.followupAnswer = "";
          
          setCaptureState("awaiting_name");
          addMira("Thank you for sharing your searching. What do people call you?", "none");
          setLoading(false);
          return;
        } else {
          // Not yet — graceful exit
          setSeekingMode(false);
          setCaptureState("idle");
          addMira("That's okay. Come back when you're ready. I'll be here. And in the meantime — read some of the other stories. Sometimes someone else's why helps you find yours.", "seeking_exit");
          setLoading(false);
          return;
        }
      }

      // Regular seeking exchange
      try {
        const res = await axios.post(`${API}/mira/seeking`, { 
          message: text.trim(), 
          session_id: seekingSessionRef.current,
          exchange_count: newCount 
        });
        seekingSessionRef.current = res.data.session_id;
        setSeekingExchanges(newCount);
        
        const reply = res.data.reply;
        seekingTranscriptRef.current.push({ role: "mira", text: reply });
        
        // After 3+ exchanges, generate a reflection
        if (newCount >= 3) {
          const transcriptText = seekingTranscriptRef.current.map(t => `${t.role === "user" ? "Them" : "Mira"}: ${t.text}`).join("\n");
          try {
            const reflectRes = await axios.post(`${API}/mira/reflect`, { conversation: transcriptText });
            const reflection = reflectRes.data.reflection;
            seekingReflectionRef.current = reflection;
            capturedStory.current.miraReflection = reflection;
            setCaptureState("seeking_reflect_offered");
            addMira(reply + "\n\nI think I'm beginning to hear something in what you've shared. " + reflection + "\n\nDoes this feel true to you?", "seeking_reflect");
          } catch {
            addMira(reply, "seeking");
          }
        } else {
          addMira(reply, "seeking");
        }
      } catch {
        addMira("What did you love before the world told you what you should love?", "seeking");
      }
      setLoading(false);
      return;
    }

    // Regular chat with Claude
    try {
      const res = await axios.post(`${API}/mira/chat`, {
        message: text.trim(),
        session_id: sessionId,
        page: location.pathname,
      });
      setSessionId(res.data.session_id);
      const reply = res.data.reply;

      const replyLower = reply.toLowerCase();
      let nextChips = "opening";
      if (replyLower.includes("iambecause") && (replyLower.includes("platform") || replyLower.includes("place") || replyLower.includes("question"))) {
        nextChips = "after_explain";
      } else if (replyLower.includes("pattern") || replyLower.includes("theme") || replyLower.includes("stories show") || replyLower.includes("learning")) {
        nextChips = "after_insight";
      }

      addMira(reply, nextChips);
    } catch {
      addMira("I seem to have lost my words for a moment. Try again?", "opening");
    } finally {
      setLoading(false);
    }
  };

  const handleSurprise = async () => {
    addUser("Surprise me, Mira");
    setLoading(true);
    try {
      const res = await axios.get(`${API}/mira/surprise`);
      const { line, name, role } = res.data;
      addMira(`Here's someone I've been thinking about.\n\n"${line}"\n\n— ${name}, ${role}`, "after_surprise");
    } catch {
      addMira("I wanted to share a story with you, but they're all shy today. Try again?", "opening");
    } finally {
      setLoading(false);
    }
  };

  const handleChip = (chip) => {
    if (chip.action === "navigate") {
      setOpen(false);
      if (chip.to.startsWith("/#")) {
        navigate("/");
        setTimeout(() => {
          document.querySelector(chip.to.replace("/", ""))?.scrollIntoView({ behavior: "smooth" });
        }, 300);
      } else {
        navigate(chip.to);
      }
    } else if (chip.action === "message") {
      sendMessage(chip.text);
    } else if (chip.action === "surprise") {
      handleSurprise();
    } else if (chip.action === "fresh") {
      clearStoredSession();
      setSessionId(null);
      setCaptureState("idle");
      setSeekingMode(false);
      capturedStory.current = { why: "", polishedWhy: "", followupAnswer: "", name: "", role: "", city: "", email: "", whatsapp: "", media_url: "", anonymous: false, spokenFields: 0, typedFields: 0, stage: null, miraReflection: "" };
      const opening = getOpeningMessage(location.pathname);
      setMessages([{ role: "mira", text: opening }]);
      setChipSet("opening");
      setShowChips(true);
    } else if (chip.action === "upload") {
      fileInputRef.current?.click();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const currentChips = CHIP_SETS[chipSet] || [];
  const showPhotoUpload = captureState === "awaiting_photo" && !uploading;

  const placeholders = {
    awaiting_role: "Your craft, your calling...",
    awaiting_why: "Take your time. There is no word limit.",
    awaiting_deep: "Take your time...",
    awaiting_name: "Your name...",
    awaiting_city: "Your city...",
    awaiting_email: "your@email.com",
    awaiting_whatsapp: "+91 98765 43210",
    awaiting_photo: "Type 'skip' or upload a photo...",
    awaiting_anon: "Use my name / Stay anonymous...",
    seeking_articulate: "Even just a beginning...",
    awaiting_seeking_role: "What you do...",
  };
  const placeholder = placeholders[captureState] || "Say something to Mira...";

  // Lock body scroll when panel is open on mobile only
  useEffect(() => {
    if (open && window.innerWidth < 640) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  // Listen for external event to open Mira in seeking mode
  useEffect(() => {
    const handler = () => {
      setOpen(true);
      // The startInSeekingMode-like behavior via opening message is handled by the reset useEffect
      // We trigger a delayed seeking mode activation
      setTimeout(() => {
        if (!seekingMode) {
          setSeekingMode(true);
          const seekingOpening = "Hello. I heard you're still searching. That's the most honest place to begin. Tell me — what are you doing right now, even if it doesn't feel like enough yet?";
          setMessages([{ role: "mira", text: seekingOpening }]);
          setChipSet("seeking");
          setShowChips(true);
          seekingTranscriptRef.current = [{ role: "mira", text: seekingOpening }];
          speakText(seekingOpening);
        }
      }, 100);
    };
    window.addEventListener("openMiraSeeking", handler);
    return () => window.removeEventListener("openMiraSeeking", handler);
  }, [seekingMode, speakText]);

  return (
    <>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handlePhotoUpload(file);
          e.target.value = "";
        }}
      />

      {/* Floating Orb */}
      {!open && (() => {
        const inLibrary = location.pathname.startsWith('/icons') || location.pathname.startsWith('/conversations') || location.pathname === '/because';
        if (inLibrary) {
          return (
            <button
              data-testid="mira-orb"
              onClick={() => setOpen(true)}
              className="fixed transition-transform duration-500 hover:scale-105"
              style={{ bottom: '28px', right: '20px', zIndex: 9999, paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
            >
              <span className="inline-flex items-center px-5 py-2.5 rounded-full text-sm font-medium" style={{ background: '#FEFCF7', border: '1.5px solid #16130F', color: '#16130F', boxShadow: '0 4px 16px rgba(22,19,15,0.12)' }}>
                ask mira
              </span>
            </button>
          );
        }
        return (
        <button
          data-testid="mira-orb"
          onClick={() => setOpen(true)}
          className="fixed flex flex-col items-center gap-1.5 group"
          style={{ bottom: '28px', right: '20px', zIndex: 9999, paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        >
          <div className="w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-transform duration-300 group-hover:scale-110 mira-orb-glow"
            style={{ background: '#E91E8C' }}>
            <SparkleIcon size={26} className="text-white" />
          </div>
          <span className="text-xs font-sans font-semibold tracking-wide" style={{ color: '#E91E8C' }}>Ask Mira</span>
        </button>
        );
      })()}

      {/* Chat Panel */}
      {open && (
        <div
          data-testid="mira-chat-panel"
          className="fixed overflow-hidden shadow-2xl flex flex-col sm:rounded-2xl"
          style={{
            zIndex: 9999,
            background: "#1a1a2e",
            /* Mobile: full width with margins, max height */
            bottom: 0, right: 0, left: 0,
            height: '100dvh',
            /* Desktop override via media query handled by className */
          }}
        >
          {/* Desktop size override */}
          <style>{`
            @media (min-width: 640px) {
              [data-testid="mira-chat-panel"] {
                bottom: 24px !important;
                right: 24px !important;
                left: auto !important;
                width: 380px !important;
                height: 520px !important;
              }
            }
            /* Block extension overlays */
            [data-testid="mira-chat-panel"] iframe,
            [data-testid="mira-chat-panel"] [class*="adobe"],
            [data-testid="mira-chat-panel"] [id*="adobe"],
            [data-testid="mira-chat-panel"] [data-id*="adobe"],
            [data-testid="mira-chat-panel"] > div:not([class]):not([data-testid]),
            #AdobeAcrobat_FireflyAssistBtn,
            .acrobat-fab-container,
            [id*="AdobeAcrobat"],
            [class*="acrobat-fab"],
            [data-testid="mira-chat-panel"] ~ [id*="Adobe"],
            [data-testid="mira-chat-panel"] ~ [class*="acrobat"] {
              display: none !important;
              visibility: hidden !important;
              opacity: 0 !important;
              pointer-events: none !important;
            }
          `}</style>
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4" style={{ background: (location.pathname.startsWith('/icons') || location.pathname.startsWith('/conversations') || location.pathname === '/because') ? "linear-gradient(135deg, #C99A2E 0%, #A87E1F 100%)" : "linear-gradient(135deg, #E91E8C 0%, #c2185b 100%)" }}>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <SparkleIcon size={18} className="text-white" />
              </div>
              <div>
                <p className="text-white font-sans font-semibold text-sm">Mira</p>
                <p className={`text-xs font-sans transition-all ${speaking ? "text-white" : "text-white/60"}`}>
                  {speaking ? (
                    <span className="inline-flex items-center gap-1.5">
                      Speaking
                      <span className="flex gap-0.5">
                        <span className="w-1 h-1 rounded-full bg-white animate-pulse" />
                        <span className="w-1 h-1 rounded-full bg-white animate-pulse" style={{ animationDelay: "0.2s" }} />
                        <span className="w-1 h-1 rounded-full bg-white animate-pulse" style={{ animationDelay: "0.4s" }} />
                      </span>
                    </span>
                  ) : "Listening..."}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid="mira-voice-toggle"
                onClick={toggleVoice}
                className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
                title={voiceOn ? "Mute Mira" : "Unmute Mira"}
              >
                {voiceOn ? <Volume2 size={14} className="text-white" /> : <VolumeX size={14} className="text-white/50" />}
              </button>
              <button
                data-testid="mira-close"
                onClick={() => setOpen(false)}
                className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
              >
                <X size={16} className="text-white" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 mira-messages">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={`mira-msg-${i}`}
                  className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-line ${
                    msg.role === "user"
                      ? "bg-[#E91E8C]/20 text-white/90 rounded-br-sm"
                      : "bg-white/8 text-white/80 rounded-bl-sm"
                  }`}
                  style={msg.role === "mira" ? { background: "rgba(255,255,255,0.06)" } : {}}
                >
                  {msg.text}
                </div>
              </div>
            ))}

            {/* Photo upload button */}
            {showPhotoUpload && (
              <div className="flex justify-start animate-fade-in" style={{ animationDuration: "0.3s" }}>
                <button
                  data-testid="mira-upload-photo"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-sans font-medium transition-all hover:scale-105"
                  style={{ border: "1px dashed rgba(233,30,140,0.4)", color: "#E91E8C", background: "rgba(233,30,140,0.06)" }}
                >
                  <Camera size={14} />
                  Upload a photo
                </button>
              </div>
            )}

            {/* Story preview card */}
            {showPreview && captureState === "awaiting_preview" && (
              <div data-testid="mira-preview-card" className="animate-fade-in" style={{ animationDuration: "0.4s" }}>
                <div style={{ border: '0.5px solid rgba(255,255,255,0.12)', background: '#fff', overflow: 'hidden' }}>
                  {capturedStory.current.media_url ? (
                    <div className="relative" style={{ aspectRatio: '16/9', overflow: 'hidden' }}>
                      <img src={capturedStory.current.media_url} alt="" className="w-full h-full object-cover" />
                      <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(193,127,36,0.08), rgba(193,127,36,0.2))' }} />
                    </div>
                  ) : (
                    <div className="flex items-center justify-center" style={{ aspectRatio: '16/9', background: '#FAEEDA' }}>
                      <span style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '64px', color: '#C17F24', opacity: 0.25, lineHeight: 1 }}>&ldquo;</span>
                    </div>
                  )}
                  <div className="p-4">
                    <p className="text-xs uppercase tracking-widest font-sans font-semibold mb-1" style={{ color: '#C17F24' }}>
                      {capturedStory.current.anonymous ? (capturedStory.current.stage === "seeking" ? "A searcher" : "A storyteller") : capturedStory.current.name}
                    </p>
                    <p className="text-xs mb-1" style={{ fontFamily: capturedStory.current.stage === "seeking" ? "'Playfair Display', Georgia, serif" : "inherit", fontStyle: capturedStory.current.stage === "seeking" ? "italic" : "normal", color: '#C17F24' }}>
                      {capturedStory.current.role}
                    </p>
                    {capturedStory.current.city && (
                      <p className="text-xs font-sans mb-3" style={{ color: '#9CA3AF' }}>{capturedStory.current.city}</p>
                    )}
                    <p dir="auto" className="line-clamp-4" style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '14px', lineHeight: '1.8', color: '#1F2937' }}>
                      &ldquo;{capturedStory.current.polishedWhy || capturedStory.current.why}&rdquo;
                    </p>
                  </div>
                </div>
                {/* Consent line */}
                <p className="mt-3 text-center leading-relaxed" style={{ fontSize: '10px', color: 'rgba(255,255,255,0.3)' }}>
                  By saving your story you agree to it being published on iambecause.com and shared on our social channels. You can ask us to remove it anytime at hello@iambecause.com.
                </p>
              </div>
            )}

            {/* Side-by-side versions (when editing) */}
            {showVersions && captureState === "awaiting_version_choice" && (
              <div data-testid="mira-versions" className="space-y-3 animate-fade-in" style={{ animationDuration: "0.4s" }}>
                <div className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.04)', border: '0.5px solid rgba(255,255,255,0.08)' }}>
                  <p className="text-xs font-sans font-semibold mb-2" style={{ color: 'rgba(255,255,255,0.35)' }}>Your original words</p>
                  <p className="text-sm leading-relaxed" style={{ fontFamily: "'Playfair Display', Georgia, serif", color: 'rgba(255,255,255,0.4)' }}>
                    {capturedStory.current.followupAnswer
                      ? capturedStory.current.why + " " + capturedStory.current.followupAnswer
                      : capturedStory.current.why}
                  </p>
                </div>
                <div className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.08)', border: '0.5px solid rgba(255,255,255,0.15)' }}>
                  <p className="text-xs font-sans font-semibold mb-2" style={{ color: 'rgba(255,255,255,0.6)' }}>As it will appear</p>
                  <p className="text-sm leading-relaxed" style={{ fontFamily: "'Playfair Display', Georgia, serif", color: 'rgba(255,255,255,0.85)' }}>
                    {capturedStory.current.polishedWhy}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2 pt-1">
                  <button onClick={() => handleChip({ action: "message", text: "Use my original words" })} className="px-3 py-2 rounded-full text-xs font-sans font-medium border transition-all hover:scale-105" style={{ borderColor: 'rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.6)', background: 'transparent' }}>Use my original</button>
                  <button onClick={() => handleChip({ action: "message", text: "Use the polished version" })} className="px-3 py-2 rounded-full text-xs font-sans font-medium transition-all hover:scale-105" style={{ background: '#C17F24', color: '#fff', border: 'none' }}>Use polished version</button>
                </div>
              </div>
            )}

            {/* Contextual chips */}
            {showChips && currentChips.length > 0 && !loading && (
              <div data-testid="mira-chips" className="flex flex-wrap gap-2 pt-1 animate-fade-in" style={{ animationDuration: "0.3s" }}>
                {currentChips.map((chip) => {
                  const isAmber = chip.style === "amber";
                  const isCharcoal = chip.style === "charcoal";
                  const chipStyle = isAmber
                    ? { background: "#C17F24", color: "#fff", border: "none" }
                    : isCharcoal
                    ? { borderColor: "rgba(255,255,255,0.25)", color: "rgba(255,255,255,0.7)", background: "transparent" }
                    : { borderColor: "rgba(233,30,140,0.3)", color: "rgba(233,30,140,0.9)", background: "rgba(233,30,140,0.08)" };
                  return (
                    <button
                      key={chip.label}
                      data-testid={`mira-chip-${chip.label.toLowerCase().replace(/[\s,?']/g, "-")}`}
                      onClick={() => handleChip(chip)}
                      className="px-3.5 py-2 rounded-full text-xs font-sans font-medium border transition-all duration-200 hover:scale-105"
                      style={chipStyle}
                    >
                      {chip.label}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Loading */}
            {(loading || uploading) && (
              <div className="flex justify-start">
                <div className="rounded-2xl rounded-bl-sm px-4 py-3 flex items-center gap-2" style={{ background: "rgba(255,255,255,0.06)" }}>
                  <div className="flex gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#E91E8C]/60 animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-[#E91E8C]/60 animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-[#E91E8C]/60 animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form onSubmit={handleSubmit} className="px-4 py-3 border-t" style={{ borderColor: "rgba(255,255,255,0.08)", paddingBottom: 'max(12px, env(safe-area-inset-bottom, 12px))' }}>
            <div className="flex items-center gap-2 rounded-xl px-3 py-1" style={{ background: "rgba(255,255,255,0.06)" }}>
              <input
                ref={inputRef}
                data-testid="mira-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={listening ? "Listening..." : placeholder}
                disabled={loading || uploading}
                className="flex-1 bg-transparent text-white/90 text-sm font-sans placeholder:text-white/25 focus:outline-none py-2.5"
              />
              {/* Mic button — only if browser supports it */}
              {hasSpeechSupport && (
                <button
                  type="button"
                  data-testid="mira-mic"
                  onClick={toggleMic}
                  disabled={loading || uploading}
                  className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${listening ? "mira-orb-glow" : "hover:bg-white/10"}`}
                  style={{ background: listening ? "#E91E8C" : "transparent" }}
                  title="Speak your why"
                >
                  <Mic size={14} className={listening ? "text-white" : "text-white/40"} />
                </button>
              )}
              <button
                type="submit"
                data-testid="mira-send"
                disabled={loading || uploading || !input.trim()}
                className="w-8 h-8 rounded-full flex items-center justify-center transition-all disabled:opacity-30"
                style={{ background: input.trim() ? "#E91E8C" : "transparent" }}
              >
                {loading || uploading ? (
                  <Loader2 size={14} className="text-white/60 animate-spin" />
                ) : (
                  <Send size={14} className="text-white" />
                )}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
